import { QueryParams } from '../../types/query-params';
import { GsbSaveRequest, GsbSaveMultiRequest, GsbSaveMappedRequest, GsbGetCodeRequest, GetTokenRequest, GsbUploadFileRequest } from '../../types/requests';
import { GsbQueryResponse, GsbSaveResponse, GsbQueryOpResponse, GsbSaveMultiResponse, GsbDefinitionResponse, GsbGetCodeResponse,  GsbAuthResponse, GsbBulkResponse, GsbUploadFileResponse } from '../../types/responses';
import { GsbApiService } from '../../api/gsb-api.service';
import { GsbUtils } from '../../utils/gsb-utils';
import { GsbBulkRequest } from '../../types/requests';

// Store a singleton instance
let serviceInstance: GsbEntityService | null = null;

export class GsbEntityService {
    private apiService: GsbApiService;
    private useCache: boolean;
    constructor(useCache: boolean = true) {
        this.apiService = GsbApiService.getInstance();
        this.useCache = useCache;
    }

    // Static method to get the singleton instance
    static getInstance(useCache: boolean = true): GsbEntityService {
        if (!serviceInstance) {
            serviceInstance = new GsbEntityService(useCache);
        }
        return serviceInstance;
    }

    reduceEntityDef(request :any, checkEntityDef:boolean = true){
        if(checkEntityDef && !GsbUtils.hasEntityDef(request)){
            throw new Error('Entity definition is required');
        }
        if(request.entityDef){
            request.entityDef = {
                id: request.entityDef.id,
                name: request.entityDef.name,
            }
        }
    }

    async getToken(request: GetTokenRequest): Promise<GsbAuthResponse> {
        
        try {
            // Use apiService for consistency
            const response = await this.apiService.callApi({
                method: 'POST',
                protocol: 'https',
                hostName: 'api',
                content: request,
                jsonResponse: true,
                noAuth: true,
                useBulk: false
            }, '/api/auth/getToken');
            
            return response as GsbAuthResponse;
        } catch (error) {
            console.error('Authentication request failed:', error);
            throw error;
        }
    }

    async refreshToken(request: any, token?: string, tenantCode?: string): Promise<GsbAuthResponse> {
        
        try {
            // Use apiService for consistency
            const response = await this.apiService.callApi({
                method: 'POST',
                protocol: 'https',
                hostName: 'api',
                content: request,
                jsonResponse: true,
                noAuth: true,
                useBulk: false
            }, '/api/auth/refreshToken', token, tenantCode);
            
            return response as GsbAuthResponse;
        } catch (error) {
            console.error('Authentication request failed:', error);
            throw error;
        }
    }


    async getById<T extends object>(definitionType: (new () => T) | string, id: string, token?: string, tenantCode?: string): Promise<T | null> {
        const req = new  QueryParams<any>(definitionType);
        req.entityId = id;
        const result = await this.get(req, token, tenantCode);
        return result.entity as T;
    }
    //todo: use te get copy method of api
    async getCopy<T extends object>(req: QueryParams<any>, token?: string, tenantCode?: string): Promise<GsbQueryResponse | null> {
        this.reduceEntityDef(req);
        if(!req.entityId){
            throw new Error('Entity id is required');
        }
        req.disableTransaction = true;
        try {
            const response = await this.apiService.callApi({
                method: 'POST',
                protocol: 'https',
                hostName: 'api',
                content: req
            }, '/api/entity/getCopy', token, tenantCode);
            return response as GsbQueryResponse;
        } catch (error) {
            console.error(`Get entity failed for ${req.entDefName}:`, error);
            throw error;
        }    }

    async get(req: QueryParams<any>, token?: string, tenantCode?: string): Promise<GsbQueryResponse> {
        if(!req.entityId){
            throw new Error('Entity id is required');
        }
        this.reduceEntityDef(req);
        req.disableTransaction = true;
        try {
            const response = await this.apiService.callApi({
                method: 'POST',
                protocol: 'https',
                hostName: 'api',
                content: req
            }, '/api/entity/get', token, tenantCode);
            return response as GsbQueryResponse;
        } catch (error) {
            console.error(`Get entity failed for ${req.entDefName}:`, error);
            throw error;
        }
    }

    async query(req: QueryParams<any>, token?: string, tenantCode?: string): Promise<GsbQueryResponse> {
        this.reduceEntityDef(req);
        req.disableTransaction = true;
        try {
            const response = await this.apiService.callApi({
                method: 'POST',
                protocol: 'https',
                hostName: 'api',
                content: req
            }, '/api/entity/query', token, tenantCode);
            return response as GsbQueryResponse;
        } catch (error) {
            console.error(`Query failed for ${req.entDefName}:`, error);
            throw error;
        }
    }

    async queryMapped(req: QueryParams<any>, token?: string, tenantCode?: string): Promise<GsbQueryResponse> {
        if(!req.mapColName && !req.propertyName && !req.propName){
            throw new Error('Property name is required');
        }
        this.reduceEntityDef(req);
        req.disableTransaction = true;
        const response = await this.apiService.callApi({
            method: 'POST',
            protocol: 'https',
            hostName: 'api',
            content: req
        }, '/api/entity/queryMapped', token, tenantCode);
        return response as GsbQueryResponse;
    }

    /**
     * Save an entity
     * @param entity The entity to save, should have _entDefName property
     * @param token Optional auth token
     * @param tenantCode Optional tenant code
     * @returns Promise with save response
     */
    async saveEnt(entity: any, token?: string, tenantCode?: string): Promise<GsbSaveResponse> {
        if(!entity){
            throw new Error('Entity is required');
        }
        const req = new GsbSaveRequest();
        if ('_entDefName' in entity) {
            req.entDefName = (entity as any)._entDefName;
        }
        req.entity = entity;
        let response = await this.save(req, token, tenantCode);
        if(response?.id){
            entity.id = response.id;
        }
        return response as GsbSaveResponse;
    }

    async save(req: GsbSaveRequest, token?: string, tenantCode?: string): Promise<GsbSaveResponse> {
        if(!req.entity){
            throw new Error('Entity is required');
        }
        this.reduceEntityDef(req);
        const response = await this.apiService.callApi({
            method: 'POST',
            protocol: 'https',
            hostName: 'api',
            content: req
        }, '/api/entity/save', token, tenantCode);
        if(response?.id){
            req.entity.id = response.id;
        }
        return response as GsbSaveResponse;
    }

    async updateQuery(req: QueryParams<any>, token?: string, tenantCode?: string): Promise<GsbQueryOpResponse> {
        this.reduceEntityDef(req);
        const response = await this.apiService.callApi({
            method: 'POST',
            protocol: 'https',
            hostName: 'api',
            content: req
        }, '/api/entity/updateQuery', token, tenantCode);
        return response as GsbQueryOpResponse;
    }

    async saveMulti(req: GsbSaveMultiRequest, token?: string, tenantCode?: string): Promise<GsbSaveMultiResponse> {
        this.reduceEntityDef(req);
        const response = await this.apiService.callApi({
            method: 'POST',
            protocol: 'https',
            hostName: 'api',
            content: req
        }, '/api/entity/saveMulti', token, tenantCode);
        return response as GsbSaveMultiResponse;
    }



    /**
     * Execute multiple operations in a single bulk request
     * @param bulkRequest The bulk request containing multiple operations
     * @param token Optional auth token
     * @param tenantCode Optional tenant code
     * @returns Promise with bulk operation results
     */
    async executeBulk(bulkRequest: GsbBulkRequest, token?: string, tenantCode?: string): Promise<GsbBulkResponse> {
        try {
            const response = await this.apiService.callApi({
                method: 'POST',
                protocol: 'https',
                hostName: 'api',
                content: bulkRequest
            }, '/api/entity/bulk', token, tenantCode);
            
            return response as GsbBulkResponse;
        } catch (error) {
            console.error('Bulk operation failed:', error);
            throw error;
        }
    }

    async getCode(req: GsbGetCodeRequest, token?: string, tenantCode?: string): Promise<GsbGetCodeResponse> {
        if(!req.codeGeneratorId){
            throw new Error('Code generator id is required');
        }
        const response = await this.apiService.callApi({
            method: 'POST',
            protocol: 'https',
            hostName: 'api',
            content: req
        }, '/api/entity/getCode', token, tenantCode);
        return response as GsbGetCodeResponse;
    }

    async saveMappedItems(req: GsbSaveMappedRequest, token?: string, tenantCode?: string): Promise<GsbSaveResponse> {
        if(!req.items){
            throw new Error('Items are required');
        }
        if(!req.propName && !req.mapColName && !req.propertyName){
            throw new Error('Property name is required');
        }
        this.reduceEntityDef(req);
        const response = await this.apiService.callApi({
            method: 'POST',
            protocol: 'https',
            hostName: 'api',
            content: req
        }, '/api/entity/saveMappedItems', token, tenantCode);
        return response as GsbSaveResponse;
    }

    async removeMappedItems(req: GsbSaveMappedRequest, token?: string, tenantCode?: string): Promise<GsbQueryOpResponse> {
        if(!req.items){
            throw new Error('Items are required');
        }
        if(!req.propName && !req.mapColName && !req.propertyName){
            throw new Error('Property name or mapped column name is required');
        }
        this.reduceEntityDef(req);
        const response = await this.apiService.callApi({
            method: 'POST',
            protocol: 'https',
            hostName: 'api',
            content: req
        }, '/api/entity/removeMappedItems', token, tenantCode);
        return response as GsbQueryOpResponse;
    }

        async getDefinition(req: { entityDef: { id?: string; name?: string } }, token?: string, tenantCode?: string): Promise<GsbDefinitionResponse> {
        this.reduceEntityDef(req);
        try {
            try {
                const response = await this.apiService.callApi({
                    method: 'POST',
                    protocol: 'https',
                    hostName: 'api',
                    content: req
                }, '/api/entityDef/get', token, tenantCode);
                return response as GsbDefinitionResponse;
            } catch (error:any) {
                return {
                    entityDef: null,
                    message: error?.message
                };
            }
        } catch (error) {
            throw error;
        }
    }

    async delete(req: GsbSaveRequest, token?: string, tenantCode?: string): Promise<GsbQueryOpResponse> {
        if(!req.entityId){
            throw new Error('Entity id is required');
        }
        this.reduceEntityDef(req);
        const response = await this.apiService.callApi({
            method: 'POST',
            protocol: 'https',
            hostName: 'api',
            content: req
        }, '/api/entity/delete', token, tenantCode);
        return response as GsbQueryOpResponse;
    }

    async deleteById(entDefName: string, id: string, token?: string, tenantCode?: string): Promise<GsbQueryOpResponse> {
        const req = new QueryParams<any>(entDefName);
        req.entityId = id;
        if(!req.entityId){
            throw new Error('Entity id is required');
        }
        this.reduceEntityDef(req);
        const response = await this.apiService.callApi({
            method: 'POST',
            protocol: 'https',
            hostName: 'api',
            content: req
        }, '/api/entity/delete', token, tenantCode);
        return response as GsbQueryOpResponse;
    }

    async deleteEnt(entity: any, token?: string, tenantCode?: string): Promise<GsbQueryOpResponse> {
        const req = new QueryParams<any>(entity._entDefName);
        req.entityId = entity.id;
        if(!req.entityId){
            throw new Error('Entity id is required');
        }
        const response = await this.apiService.callApi({
            method: 'POST',
            protocol: 'https',
            hostName: 'api',
            content: req
        }, '/api/entity/delete', token, tenantCode);
        return response as GsbQueryOpResponse;
    }   

    async deleteQuery(req: QueryParams<any>, token?: string, tenantCode?: string): Promise<GsbQueryOpResponse> {
        this.reduceEntityDef(req);
        const response = await this.apiService.callApi({
            method: 'POST',
            protocol: 'https',
            hostName: 'api',
            content: req
        }, '/api/entity/deleteQuery', token, tenantCode);
        return response as GsbQueryOpResponse;
    }

    async runWorkflow(req: any, token?: string, tenantCode?: string): Promise<any> {

        return this.apiService.callApi({
            method: 'POST',
            protocol: 'https',
            hostName: 'api',
            content: req,
            useBulk: false
        }, '/api/workflow/runWorkflow', token, tenantCode);
    }

    async startWorkflow(req: any, token?: string, tenantCode?: string): Promise<any> {
        return this.apiService.callApi({
            method: 'POST',
            protocol: 'https',
            hostName: 'api',
            content: req,
            useBulk: false
        }, '/api/workflow/startWorkflow', token, tenantCode);
    }

    async runWfFunction(req: any, token?: string, tenantCode?: string): Promise<any> {
        return this.apiService.callApi({
            method: 'POST',
            protocol: 'https',
            hostName: 'api',
            content: req,
            useBulk: false
        }, '/api/workflow/runFunction', token, tenantCode);
    }

    async testWfFunction(req: any, token?: string, tenantCode?: string): Promise<any> {
        return this.apiService.callApi({
            method: 'POST',
            protocol: 'https',
            hostName: 'api',
            content: req,
            useBulk: false
        }, '/api/workflow/testFunction', token, tenantCode);
    }


    async iterateTask(req: any, token?: string, tenantCode?: string): Promise<any> {
        return this.apiService.callApi({
            method: 'POST',
            protocol: 'https',
            hostName: 'api',
            content: req,
            useBulk: false
        }, '/api/workflow/iterateTask', token, tenantCode);
    }

    async changePassword(req: any, token?: string, tenantCode?: string): Promise<any> {
        return this.apiService.callApi({
            method: 'POST',
            protocol: 'https',
            hostName: 'api',
            content: req,
            useBulk: false
        }, '/api/auth/changePassword', token, tenantCode);
    }

    async uploadFile(req: GsbUploadFileRequest, token?: string, tenantCode?: string): Promise<GsbUploadFileResponse> {
        // Prepare content for FormData conversion
        
        return this.apiService.callApi({
            method: 'POST',
            protocol: 'https',
            hostName: 'api',
            content: req,
            isFormData: true,
            useBulk: false,
        }, '/api/file/upload', token, tenantCode);
    }
}

